import { useEffect, useState } from "react";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Input,
  Select,
  Tabs,
  TabsItem,
  Textarea,
} from "@/components";
import { axiosInstance, handleAxiosError, type ApiResponse } from "@/helpers/axios";
import type { Account } from "@/app/accounts/_types/accounts.types";
import type { Group } from "@/app/groups/_types/groups.types";
import type { ApiDevice } from "../../_types/devices.types";
import { useCreateDeviceMutation } from "../../_hooks/useCreateDeviceMutation";
import { DEVICE_PLATFORM_OPTIONS } from "../../_constants/devices.const";
import {
  CREATE_DEVICE_TAB_LABELS,
  NONE_VALUE,
  accountAssignmentLabel,
  accountAssignmentValue,
  buildCreateDevicePayload,
  uniqueSortedStrings,
  validateCreateDeviceFields,
  type CreateDeviceTab,
  type PlacementOption,
} from "./create-device-drawer.helpers";
import styles from "./CreateDeviceDrawer.module.scss";

type CreateDeviceDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const CreateDeviceDrawer = ({ isOpen, onClose }: CreateDeviceDrawerProps) => {
  const createMutation = useCreateDeviceMutation();
  const [tab, setTab] = useState<CreateDeviceTab>("identity");
  const [invalidTab, setInvalidTab] = useState<CreateDeviceTab | null>(null);

  const [name, setName] = useState("");
  const [deviceType, setDeviceType] = useState("tablet");
  const [notes, setNotes] = useState("");
  const [location, setLocation] = useState(NONE_VALUE);
  const [organization, setOrganization] = useState(NONE_VALUE);
  const [assignedTo, setAssignedTo] = useState(NONE_VALUE);

  const [locationOptions, setLocationOptions] = useState<PlacementOption[]>([]);
  const [organizationOptions, setOrganizationOptions] = useState<PlacementOption[]>([]);
  const [assigneeOptions, setAssigneeOptions] = useState<PlacementOption[]>([]);
  const [placementLoading, setPlacementLoading] = useState(false);
  const [placementError, setPlacementError] = useState<string | null>(null);

  const [nameError, setNameError] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ enrollmentCode: string; deviceId: string } | null>(
    null,
  );

  const resetForm = () => {
    setTab("identity");
    setInvalidTab(null);
    setName("");
    setDeviceType("tablet");
    setNotes("");
    setLocation(NONE_VALUE);
    setOrganization(NONE_VALUE);
    setAssignedTo(NONE_VALUE);
    setNameError("");
    setSubmitError(null);
    setCreated(null);
    setPlacementError(null);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const loadPlacementOptions = async () => {
      setPlacementLoading(true);
      setPlacementError(null);
      try {
        const [devicesRes, groupsRes, accountsRes] = await Promise.all([
          axiosInstance.get<ApiResponse<ApiDevice[]>>("/v2/devices", {
            params: {
              page: 1,
              limit: 100,
              deviceType: "all",
              deviceStatus: "all",
              enrollment: "all",
              isActive: "1",
            },
          }),
          axiosInstance.get<ApiResponse<{ items: Group[] }>>("/v2/groups", {
            params: { kind: "all", archived: "0" },
          }),
          axiosInstance.get<ApiResponse<Account[]>>("/v2/accounts/customers", {
            params: { page: 1, limit: 100, role: "all", subscription: "all", period: "all" },
          }),
        ]);
        if (cancelled) return;

        const devices = devicesRes.data.data ?? [];
        const locations = uniqueSortedStrings(devices.map((d) => d.location)).map((value) => ({
          value,
          label: value,
        }));
        setLocationOptions(locations);

        const groups = groupsRes.data.data?.items ?? [];
        setOrganizationOptions(
          groups
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((g) => ({ value: g.name, label: `${g.name} (${g.kind})` })),
        );

        const accounts = accountsRes.data.data ?? [];
        setAssigneeOptions(
          accounts
            .slice()
            .sort((a, b) => a.email.localeCompare(b.email))
            .map((a) => ({
              value: accountAssignmentValue(a),
              label: accountAssignmentLabel(a),
            })),
        );
      } catch {
        if (!cancelled) {
          setPlacementError("Could not load placement options.");
          setLocationOptions([]);
          setOrganizationOptions([]);
          setAssigneeOptions([]);
        }
      } finally {
        if (!cancelled) setPlacementLoading(false);
      }
    };

    void loadPlacementOptions();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleClose = () => {
    if (createMutation.isPending) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const validation = validateCreateDeviceFields({ name });
    if (!validation.ok) {
      setNameError(validation.nameError);
      setInvalidTab(validation.tab);
      setTab(validation.tab);
      return;
    }
    setNameError("");
    setInvalidTab(null);
    setSubmitError(null);
    try {
      const data = await createMutation.mutateAsync(
        buildCreateDevicePayload({
          name,
          deviceType,
          location,
          organization,
          assignedTo,
          notes,
        }),
      );
      setCreated({ enrollmentCode: data.enrollmentCode, deviceId: data.id });
    } catch (err) {
      setSubmitError(handleAxiosError(err)?.message ?? "Could not create device.");
    }
  };

  const enrollmentPageUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/devices/enroll`
      : "/devices/enroll";

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  const busy = createMutation.isPending;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Create device">
      <DrawerHeader
        title="Create device"
        onClose={handleClose}
        closeDisabled={busy}
      />
      {created ? (
        <div className={styles["comp__stack"]}>
          <DrawerBody>
            <div className={styles["comp"]}>
              <div className={styles["comp__success"]}>
                <strong>Device created.</strong> Use this one-time enrollment code on the MDM agent:
                <p className={styles["comp__code"]}>{created.enrollmentCode}</p>
                <div className={styles["comp__actions"]}>
                  <Button
                    type="button"
                    size="small"
                    variant="secondary"
                    onClick={() => copyToClipboard(created.enrollmentCode)}
                  >
                    Copy code
                  </Button>
                  <Button
                    type="button"
                    size="small"
                    variant="secondary"
                    onClick={() => copyToClipboard(enrollmentPageUrl)}
                  >
                    Copy enroll page link
                  </Button>
                </div>
                <p style={{ marginTop: "0.75rem", fontSize: "0.8125rem", color: "var(--color-text-secondary)" }}>
                  Enroll instructions: {enrollmentPageUrl}
                </p>
              </div>
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button type="button" variant="primary" size="medium" onClick={handleClose}>
              Done
            </Button>
          </DrawerFooter>
        </div>
      ) : (
        <form className={styles["comp__stack"]} onSubmit={handleSubmit}>
          <DrawerBody className={styles["comp__body"]}>
            <div className={styles["comp"]}>
              <div className={styles["comp__tabs-wrap"]}>
                <Tabs
                  value={tab}
                  onChange={(v) => setTab(v as CreateDeviceTab)}
                  variant="secondary"
                  size="medium"
                >
                  {(Object.keys(CREATE_DEVICE_TAB_LABELS) as CreateDeviceTab[]).map((key) => (
                    <TabsItem
                      key={key}
                      value={key}
                      className={
                        invalidTab === key ? styles["comp__tab-error"] : undefined
                      }
                      endContent={invalidTab === key ? "•" : undefined}
                    >
                      {CREATE_DEVICE_TAB_LABELS[key]}
                    </TabsItem>
                  ))}
                </Tabs>
              </div>

              {tab === "identity" ? (
                <div className={styles["comp__panel"]} role="tabpanel">
                  <div className={styles["comp__intro"]}>
                    <h3 className={styles["comp__section-title"]}>Identity</h3>
                    <p className={styles["comp__subtitle"]}>
                      Name and form factor for the new device record. Notes are optional context
                      for operators.
                    </p>
                  </div>
                  <div className={styles["comp__panel-content"]}>
                    <div className={styles["comp__fields"]}>
                      <div className={styles["comp__row"]}>
                        <Input
                          id="create-device-name"
                          label="Name"
                          value={name}
                          placeholder="e.g. Clinic tablet 1"
                          required
                          disabled={busy}
                          error={nameError}
                          onChange={(v) => {
                            setName(v);
                            setNameError("");
                            setInvalidTab(null);
                            setSubmitError(null);
                          }}
                        />
                        <Select
                          id="create-device-device-type"
                          label="Device type"
                          fullWidth
                          size="medium"
                          variant="secondary"
                          value={deviceType}
                          disabled={busy}
                          onChange={(v) => setDeviceType(String(v))}
                        >
                          {DEVICE_PLATFORM_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <Textarea
                        id="create-device-notes"
                        label="Description / notes"
                        value={notes}
                        placeholder="Optional"
                        disabled={busy}
                        rows={4}
                        onChange={(v) => setNotes(v)}
                      />
                    </div>
                  </div>
                </div>
              ) : null}

              {tab === "placement" ? (
                <div className={styles["comp__panel"]} role="tabpanel">
                  <div className={styles["comp__intro"]}>
                    <h3 className={styles["comp__section-title"]}>Placement</h3>
                    <p className={styles["comp__subtitle"]}>
                      Location is optional (None when none exist yet). Organization is a plain
                      string — group names are suggestions only, not membership. Assignee uses
                      account labels. Prototype profiles and branding cannot be assigned here.
                    </p>
                  </div>
                  <div className={styles["comp__panel-content"]}>
                    {placementLoading ? (
                      <p className={styles["comp__hint"]}>Loading placement options…</p>
                    ) : null}
                    {placementError ? (
                      <p className={styles["comp__error"]}>{placementError}</p>
                    ) : null}
                    <div className={styles["comp__fields"]}>
                      <Select
                        id="create-device-location"
                        label="Location"
                        fullWidth
                        size="medium"
                        variant="secondary"
                        value={location}
                        disabled={busy || placementLoading}
                        onChange={(v) => setLocation(String(v))}
                      >
                        <option value={NONE_VALUE}>None</option>
                        {locationOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <Select
                        id="create-device-organization"
                        label="Organization"
                        fullWidth
                        size="medium"
                        variant="secondary"
                        value={organization}
                        disabled={busy || placementLoading}
                        onChange={(v) => setOrganization(String(v))}
                      >
                        <option value={NONE_VALUE}>None</option>
                        {organizationOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      <Select
                        id="create-device-assigned-to"
                        label="Assigned to"
                        fullWidth
                        size="medium"
                        variant="secondary"
                        value={assignedTo}
                        disabled={busy || placementLoading}
                        onChange={(v) => setAssignedTo(String(v))}
                      >
                        <option value={NONE_VALUE}>None</option>
                        {assigneeOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </Select>
                      {!placementLoading &&
                      locationOptions.length === 0 &&
                      organizationOptions.length === 0 &&
                      assigneeOptions.length === 0 &&
                      !placementError ? (
                        <p className={styles["comp__hint"]}>
                          No groups, accounts, or prior locations available yet. You can still create
                          the device with identity only.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              {submitError ? <div className={styles["comp__error"]}>{submitError}</div> : null}
            </div>
          </DrawerBody>
          <DrawerFooter>
            <Button type="button" variant="secondary" onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={busy}>
              {busy ? "Creating…" : "Create device"}
            </Button>
          </DrawerFooter>
        </form>
      )}
    </Drawer>
  );
};
